// reasoning.ts — pure helpers for surfacing agent reasoning at the
// slip-leg level (FreeSuperTips Logic 6 — Per-Leg Reasoning).
//
// Doctrine, per the Logics doc §9:
//   "Never publish per-leg reasoning that reveals STRIKZA's specific
//    agent methodology, thresholds, or scoring systems. Two sentences
//    of finding — never mechanism."
//
// These helpers cap reasoning to ~2 sentences / 240 chars and scan
// for banned methodology vocabulary so a doctrinal-violation never
// leaks past the render boundary.

const SENTENCE_BOUNDARY = /([.!?])\s+/;
const MAX_LEN = 240;
const MAX_SENTENCES = 2;

/** Forbidden vocab — methodology / threshold / mechanism names that
 *  must never surface in user-facing reasoning. Curated from
 *  STRIKZA_FreeSuperTips_Intelligence_Logics doc §9 and from common
 *  internal terms. Order is irrelevant — matching uses word
 *  boundaries (no substring false positives like "agent c" matching
 *  "agent confirms"), and the scanner picks the LONGEST matching
 *  term so "gpt-4o-mini" beats "gpt-4o". */
export const FORBIDDEN_REASONING_VOCAB: readonly string[] = [
  // Agent-by-letter naming
  "agent a",
  "agent b",
  "agent c",
  "agent d",
  "agent e",
  "swarm 1",
  "swarm 2",
  "swarm1",
  "swarm2",
  "consensus ranker",
  "debate moderator",
  "pattern memory",
  "scout agent",
  "acca architect",
  // Mechanism names
  "kelly fraction",
  "kelly criterion",
  "phase withdrawal",
  "phase 1a",
  "exploitability score",
  "composite score",
  "circuit breaker",
  "cost ceiling",
  "throttle",
  "fair_price_outputs",
  "fair_prob",
  "rg_audit_events",
  "desk_picks",
  "clv_consensus",
  "model_inputs",
  "poisson distribution",
  "elo rating",
  // Thresholds / numbers tied to internal config
  "min_confidence",
  "abstain_min",
  "min_ev_pct",
  // Provider / model leaks
  "gpt-4o",
  "gpt-4o-mini",
  "claude-",
  "openai",
  "anthropic",
];

// Escape a string for safe use inside a RegExp. Mirrors lodash.escapeRegExp.
function escapeForRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Build a precompiled, word-boundary-aware matcher. Terms are sorted
// longest-first so a multi-word phrase like "gpt-4o-mini" matches
// before its prefix "gpt-4o". Non-alphanumeric chars (`_`, `-`, ` `)
// already act as natural separators inside the source strings — we
// add `\b` only on the alphanumeric edges.
const FORBIDDEN_PATTERNS: ReadonlyArray<{ term: string; re: RegExp }> = [
  ...FORBIDDEN_REASONING_VOCAB,
]
  .slice()
  .sort((a, b) => b.length - a.length)
  .map((term) => {
    const escaped = escapeForRegex(term);
    const startsWithAlnum = /^[a-z0-9]/i.test(term);
    const endsWithAlnum = /[a-z0-9]$/i.test(term);
    const left = startsWithAlnum ? "\\b" : "";
    const right = endsWithAlnum ? "\\b" : "";
    return { term, re: new RegExp(`${left}${escaped}${right}`, "i") };
  });

export interface ReasoningCheck {
  ok: boolean;
  /** When ok=false, the matched forbidden term (lowercase). */
  violation?: string;
}

/** Returns ok=false (with the matched term) when the input contains
 *  any forbidden methodology vocabulary. Case-insensitive,
 *  word-boundary-aware match. Tested longest-first so phrases beat
 *  their prefixes (e.g. "gpt-4o-mini" beats "gpt-4o"). */
export function checkReasoningSafe(text: string | null | undefined): ReasoningCheck {
  if (!text) return { ok: true };
  for (const { term, re } of FORBIDDEN_PATTERNS) {
    if (re.test(text)) return { ok: false, violation: term };
  }
  return { ok: true };
}

/** Trim agent reasoning to a 2-sentence preview, ≤ 240 chars, ending
 *  on a sentence boundary where possible. Returns null when input is
 *  empty or unsafe (forbidden vocab). The render layer is then
 *  responsible for hiding the line entirely — never replacing it
 *  with a placeholder string. */
export function summarizeReasoning(text: string | null | undefined): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;

  const safety = checkReasoningSafe(trimmed);
  if (!safety.ok) return null;

  // Split on sentence boundaries; preserve the punctuation.
  const parts = trimmed.split(SENTENCE_BOUNDARY);
  // parts has alternating [sentence, punct, sentence, punct, …, lastSentence?]
  let out = "";
  let sentenceCount = 0;
  for (let i = 0; i < parts.length; i += 2) {
    const sentence = parts[i] ?? "";
    const punct = parts[i + 1] ?? "";
    if (!sentence) continue;
    const next = `${out}${out.length > 0 ? " " : ""}${sentence}${punct}`;
    if (next.length > MAX_LEN) {
      // Truncate to MAX_LEN and add an ellipsis if we have nothing yet,
      // otherwise stop at the prior sentence boundary.
      if (out.length === 0) return `${sentence.slice(0, MAX_LEN - 1).trimEnd()}…`;
      break;
    }
    out = next;
    sentenceCount += 1;
    if (sentenceCount >= MAX_SENTENCES) break;
  }
  return out.length > 0 ? out : null;
}
